// Calques SVG d'une variante (§ 8.1 de la spécification) : pièces teintées selon la couverture, unités intérieures à l'échelle,
// groupes extérieurs, réseau frigorifique (couleur + motif + étiquette par diamètre), dérivations et passages, condensats, bus et
// alimentations, pastilles des points de contrôle. Couleurs fixes : le plan est un papier blanc dans les deux thèmes.
import type { ReactNode } from 'react';
import { familleUi, type Pt, type VarianteProposition } from '@shared/proposition/types.ts';
import { CALQUES_PROPOSITION, TEINTES_PIECE, type CalqueProposition } from '@shared/proposition/presentation.ts';
import {
  ancrageTrace, centre, cleDiametre, decalages, etatPiece, kw, motifEchelle, pct, type PieceVue, type PointLocalise, type StyleDiametre,
} from './logique.ts';

export const CALQUES = CALQUES_PROPOSITION;
export type Calque = CalqueProposition;
export type Selection = { type: 'unite' | 'troncon' | 'ue' | 'piece' | 'noeud' | 'condensat'; id: string } | null;

/** Teintes des pièces : les mêmes à l'écran et dans le PDF du plan annoté (shared/proposition/presentation.ts). */
export const TEINTES = TEINTES_PIECE;
const pts = (l: Pt[]) => l.map((q) => q.join(',')).join(' ');

interface Props {
  v: VarianteProposition; planId: string; u: number; pieces: PieceVue[]; actifs: Set<Calque>; styles: Map<string, StyleDiametre>;
  points: PointLocalise[]; pointChoisi: string | null; choisirPoint: (id: string) => void;
  selection: Selection; choisir: (s: Selection) => void; nomPlan: (planId: string) => string;
}

/** Pictogramme d'une unité intérieure dans son emprise (à l'échelle réelle du panneau ou de l'unité). */
function Pictogramme({ type, emprise, x, y, u }: { type: string; emprise: Pt[] | null; x: number; y: number; u: number }) {
  const e = emprise && emprise.length >= 3 ? emprise : [[x - 18 * u, y - 18 * u], [x + 18 * u, y - 18 * u], [x + 18 * u, y + 18 * u], [x - 18 * u, y + 18 * u]] as Pt[];
  const f = familleUi(type as never);
  const [a, b, c, d] = e;
  const mil = (p: Pt, q: Pt): Pt => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
  const sw = 1.6 * u;
  return (
    <g className="prp-picto">
      <polygon points={pts(e)} fill="#FFFFFF" stroke="#111827" strokeWidth={sw} />
      {f === 'cassette' && <><line x1={a[0]} y1={a[1]} x2={c[0]} y2={c[1]} stroke="#111827" strokeWidth={sw} /><line x1={b[0]} y1={b[1]} x2={d[0]} y2={d[1]} stroke="#111827" strokeWidth={sw} /></>}
      {(f === 'gainable' || f === 'gainable_hp') && [0.25, 0.5, 0.75].map((t) => {
        const p1: Pt = [a[0] + (d[0] - a[0]) * t, a[1] + (d[1] - a[1]) * t]; const p2: Pt = [b[0] + (c[0] - b[0]) * t, b[1] + (c[1] - b[1]) * t];
        return <line key={t} x1={p1[0]} y1={p1[1]} x2={p2[0]} y2={p2[1]} stroke="#111827" strokeWidth={f === 'gainable_hp' ? sw * 2 : sw} />;
      })}
      {f === 'murale' && <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="#111827" strokeWidth={sw * 3} />}
      {f === 'console' && <><line x1={d[0]} y1={d[1]} x2={c[0]} y2={c[1]} stroke="#111827" strokeWidth={sw * 3} /><circle cx={mil(a, c)[0]} cy={mil(a, c)[1]} r={3 * u} fill="#111827" /></>}
    </g>
  );
}

function Etiquette({ x, y, u, texte, sous, ton = 'neutre' }: { x: number; y: number; u: number; texte: string; sous?: string; ton?: 'neutre' | 'ue' }) {
  const l = Math.max(texte.length, (sous?.length ?? 0) * 0.85) * 7.2 * u + 12 * u;
  const h = (sous ? 34 : 20) * u;
  return (
    <g className="prp-etiquette" pointerEvents="none">
      <rect x={x - l / 2} y={y - h} width={l} height={h} rx={4 * u} fill={ton === 'ue' ? '#111827' : '#FFFFFF'} stroke="#111827" strokeWidth={1.2 * u} />
      <text x={x} y={y - h + 14 * u} fontSize={12 * u} textAnchor="middle" fontWeight={600} fill={ton === 'ue' ? '#FFFFFF' : '#111827'}>{texte}</text>
      {sous && <text x={x} y={y - h + 28 * u} fontSize={10.5 * u} textAnchor="middle" fill={ton === 'ue' ? '#E8EDF4' : '#3B4656'}>{sous}</text>}
    </g>
  );
}

export function DefsCalques() {
  return (
    <defs>
      <pattern id="prp-hachures" patternUnits="userSpaceOnUse" width="24" height="24" patternTransform="rotate(45)">
        <rect width="24" height="24" fill="rgba(255,241,214,0.55)" /><line x1="0" y1="0" x2="0" y2="24" stroke="#8A4B00" strokeWidth="5" strokeOpacity="0.55" />
      </pattern>
    </defs>
  );
}

export function CalquesVariante({ v, planId, u, pieces, actifs, styles, points, pointChoisi, choisirPoint, selection, choisir, nomPlan }: Props) {
  const ici = <T extends { planId: string }>(l: T[]) => l.filter((x) => x.planId === planId);
  const surPlan = pieces.filter((p) => p.planId === planId);
  const choisi = (type: NonNullable<Selection>['type'], id: string) => selection?.type === type && selection.id === id;
  const clic = (s: Selection) => (e: { stopPropagation: () => void }) => { e.stopPropagation(); choisir(s); };
  const cles = (s: Selection) => (e: { key: string; preventDefault: () => void }) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choisir(s); } };
  const couches: ReactNode[] = [];

  if (actifs.has('pieces')) couches.push(
    <g key="pieces" className="prp-c-pieces">
      {surPlan.map((p) => {
        const etat = etatPiece(v.besoins.find((b) => b.pieceId === p.id), v.affectations.find((a) => a.pieceId === p.id));
        const t = TEINTES[etat.ton]; const [cx, cy] = centre(p.polygone);
        return (
          <g key={p.id} className="prp-piece" role="button" tabIndex={0} aria-label={`${p.nom} : ${etat.texte}`} onClick={clic({ type: 'piece', id: p.id })} onKeyDown={cles({ type: 'piece', id: p.id })}>
            <polygon points={pts(p.polygone)} fill={t.fond} stroke={t.trait} strokeWidth={(choisi('piece', p.id) ? 4 : 2) * u} strokeDasharray={etat.ton === 'incomplet' ? `${8 * u} ${5 * u}` : undefined} />
            <text x={cx} y={cy - 6 * u} fontSize={13 * u} textAnchor="middle" fontWeight={600} className="prp-texte-plan">{p.nom}</text>
            <text x={cx} y={cy + 11 * u} fontSize={11.5 * u} textAnchor="middle" className="prp-texte-plan" fill={t.trait}>{etat.ton === 'incomplet' ? 'Besoin incomplet' : etat.texte}</text>
          </g>
        );
      })}
    </g>,
  );

  if (actifs.has('unites')) couches.push(
    <g key="unites" className="prp-c-unites">
      {ici(v.unites).map((x) => x.zoneCouverte && <polygon key={`z${x.id}`} points={pts(x.zoneCouverte)} fill="rgba(0,127,131,0.07)" stroke="none" pointerEvents="none" />)}
      {ici(v.gaines).map((g) => <polyline key={g.id} points={pts(g.trace)} fill="none" stroke="#5B6676" strokeWidth={6 * u} strokeOpacity={0.45} pointerEvents="none" />)}
      {ici(v.bouches).map((b) => <rect key={b.id} x={b.x - 8 * u} y={b.y - 8 * u} width={16 * u} height={16 * u} fill="#FFFFFF" stroke="#5B6676" strokeWidth={1.5 * u} strokeDasharray={b.indicative ? `${3 * u} ${2 * u}` : undefined} pointerEvents="none" />)}
      {ici(v.unites).map((x) => (
        <g key={x.id} className={`prp-unite ${choisi('unite', x.id) ? 'est-choisi' : ''}`} role="button" tabIndex={0}
          aria-label={`Unité ${x.repere}, ${x.reference}, ${kw(x.froidW)} en froid`} onClick={clic({ type: 'unite', id: x.id })} onKeyDown={cles({ type: 'unite', id: x.id })}>
          {x.degagement && <polygon points={pts(x.degagement)} fill="none" stroke="#5B6676" strokeWidth={1.2 * u} strokeDasharray={`${5 * u} ${4 * u}`} />}
          <circle cx={x.x} cy={x.y} r={24 * u} fill="transparent" />
          <Pictogramme type={x.type} emprise={x.emprise} x={x.x} y={x.y} u={u} />
          {choisi('unite', x.id) && <circle cx={x.x} cy={x.y} r={26 * u} fill="none" stroke="#0B5FFF" strokeWidth={3 * u} />}
          <Etiquette x={x.x} y={x.y - 20 * u} u={u} texte={x.repere} sous={u < 2.2 || choisi('unite', x.id) ? `${x.reference} · ${kw(x.froidW)}` : undefined} />
        </g>
      ))}
    </g>,
  );

  if (actifs.has('ue')) couches.push(
    <g key="ue" className="prp-c-ue">
      {ici(v.groupesExterieurs).map((g) => {
        const s = v.systemes.find((x) => x.id === g.systemeId);
        const e = g.emprise ?? [[g.x - 30 * u, g.y - 20 * u], [g.x + 30 * u, g.y - 20 * u], [g.x + 30 * u, g.y + 20 * u], [g.x - 30 * u, g.y + 20 * u]] as Pt[];
        return (
          <g key={g.id} className="prp-ue" role="button" tabIndex={0} aria-label={`Groupe extérieur ${g.repere}, ${g.reference}`} onClick={clic({ type: 'ue', id: g.id })} onKeyDown={cles({ type: 'ue', id: g.id })}>
            {g.degagement && <polygon points={pts(g.degagement)} fill="none" stroke="#111827" strokeWidth={1.2 * u} strokeDasharray={`${6 * u} ${4 * u}`} />}
            <polygon points={pts(e)} fill="#D9DEE5" stroke="#111827" strokeWidth={(choisi('ue', g.id) ? 4 : 2) * u} />
            <circle cx={g.x} cy={g.y} r={Math.max(6 * u, 1)} fill="#111827" />
            <Etiquette x={g.x} y={g.y - 30 * u} u={u} ton="ue" texte={`${g.repere} · ${g.reference}`} sous={s?.tauxRaccordementPct != null ? `taux ${Math.round(s.tauxRaccordementPct)} %` : 'taux à calculer'} />
          </g>
        );
      })}
    </g>,
  );

  if (actifs.has('reseau')) couches.push(
    <g key="reseau" className="prp-c-reseau">
      {ici(v.troncons).filter((t) => t.trace.length > 1).map((t) => {
        const st = styles.get(cleDiametre(t));
        const a = ancrageTrace(t.trace);
        const sel = choisi('troncon', t.id);
        return (
          <g key={t.id} className="prp-troncon" role="button" tabIndex={0}
            aria-label={`Tronçon ${st?.libelle ?? ''}, somme des indices en aval ${t.sommeIndicesAval ?? 'inconnue'}`} onClick={clic({ type: 'troncon', id: t.id })} onKeyDown={cles({ type: 'troncon', id: t.id })}>
            <title>{`${st?.libelle ?? ''} — Σ indices en aval : ${t.sommeIndicesAval ?? 'inconnue'}`}</title>
            <polyline points={pts(t.trace)} fill="none" stroke="transparent" strokeWidth={20 * u} />
            {sel && <polyline points={pts(t.trace)} fill="none" stroke="#0B5FFF" strokeOpacity={0.35} strokeWidth={12 * u} strokeLinejoin="round" />}
            <polyline points={pts(t.trace)} fill="none" stroke={st?.couleur ?? '#111827'} strokeWidth={(t.categorie === 'vers_ui' ? 3 : 4.5) * u}
              strokeDasharray={motifEchelle(st?.motif ?? '', u)} strokeLinejoin="round" strokeLinecap="butt" />
            {a && <text x={a[0]} y={a[1] - 6 * u} fontSize={10.5 * u} textAnchor="middle" className="prp-texte-plan" fill={st?.couleur ?? '#111827'} fontWeight={600}>
              {st?.libelle.replace(/^L /, '').replace(' · G ', ' / ')}{sel && t.sommeIndicesAval != null ? ` · Σ ${t.sommeIndicesAval}` : ''}
            </text>}
          </g>
        );
      })}
    </g>,
  );

  if (actifs.has('derivations')) couches.push(
    <g key="derivations" className="prp-c-noeuds">
      {ici(v.noeuds).filter((n) => n.type === 'derivation' || n.type === 'collecteur' || n.type === 'passage').map((n) => {
        if (n.type === 'passage') {
          const autre = v.noeuds.find((m) => m.type === 'passage' && m.systemeId === n.systemeId && m.planId !== n.planId);
          return (
            <g key={n.id} className="prp-passage" pointerEvents="none">
              <circle cx={n.x} cy={n.y} r={11 * u} fill="#FFFFFF" stroke="#111827" strokeWidth={2 * u} />
              <path d={`M ${n.x} ${n.y - 7 * u} L ${n.x - 5 * u} ${n.y} M ${n.x} ${n.y - 7 * u} L ${n.x + 5 * u} ${n.y} M ${n.x} ${n.y - 7 * u} L ${n.x} ${n.y + 7 * u}`} stroke="#111827" strokeWidth={2 * u} fill="none" />
              <text x={n.x + 14 * u} y={n.y + 4 * u} fontSize={11 * u} className="prp-texte-plan" fontWeight={600}>{autre ? `vers ${nomPlan(autre.planId)}` : 'passage entre étages'}</text>
            </g>
          );
        }
        const s = 8 * u;
        return (
          <g key={n.id} className="prp-derivation" role="button" tabIndex={0} aria-label={`${n.type === 'collecteur' ? 'Collecteur' : 'Dérivation'} ${n.kit?.reference ?? 'référence à choisir'}`}
            onClick={clic({ type: 'noeud', id: n.id })} onKeyDown={cles({ type: 'noeud', id: n.id })}>
            <circle cx={n.x} cy={n.y} r={18 * u} fill="transparent" />
            {n.type === 'collecteur'
              ? <rect x={n.x - s} y={n.y - s / 2} width={2 * s} height={s} fill="#FFFFFF" stroke="#111827" strokeWidth={2 * u} />
              : <polygon points={pts([[n.x, n.y - s], [n.x + s, n.y], [n.x, n.y + s], [n.x - s, n.y]])} fill="#FFFFFF" stroke="#111827" strokeWidth={2 * u} />}
            <text x={n.x + 11 * u} y={n.y - 9 * u} fontSize={10.5 * u} className="prp-texte-plan prp-mono" fontWeight={600}>{n.kit?.reference ?? 'à choisir'}</text>
          </g>
        );
      })}
    </g>,
  );

  if (actifs.has('condensats')) couches.push(
    <g key="condensats" className="prp-c-condensats">
      {ici(v.condensats).map((c) => {
        const fin = c.trace.at(-1);
        return (
          <g key={c.id} role="button" tabIndex={0} aria-label={`Condensats de ${v.unites.find((x) => x.id === c.uniteId)?.repere ?? 'l’unité'}`} onClick={clic({ type: 'condensat', id: c.id })} onKeyDown={cles({ type: 'condensat', id: c.id })}>
            <polyline points={pts(c.trace)} fill="none" stroke="transparent" strokeWidth={16 * u} />
            <polyline points={pts(c.trace)} fill="none" stroke="#0B6FD6" strokeWidth={2 * u} strokeDasharray={`${7 * u} ${4 * u}`} />
            {fin && <circle cx={fin[0]} cy={fin[1]} r={5 * u} fill="#0B6FD6" />}
            {c.pompe !== 'non' && c.trace[0] && (
              <g pointerEvents="none"><rect x={c.trace[0][0] + 10 * u} y={c.trace[0][1] + 6 * u} width={18 * u} height={16 * u} rx={3 * u} fill="#0B6FD6" />
                <text x={c.trace[0][0] + 19 * u} y={c.trace[0][1] + 18 * u} fontSize={11 * u} textAnchor="middle" fill="#FFFFFF" fontWeight={700}>P</text></g>
            )}
          </g>
        );
      })}
    </g>,
  );

  if (actifs.has('bus')) couches.push(
    <g key="bus" className="prp-c-bus" pointerEvents="none">
      {ici(v.electricite).map((e) => <polyline key={e.id} points={pts(e.trace)} fill="none" stroke="#C62828" strokeWidth={1.5 * u} strokeDasharray={`${2 * u} ${3 * u}`} />)}
      {v.bus.flatMap((b) => b.traces.filter((t) => t.planId === planId).map((t, i) => <polyline key={`${b.systemeId}-${i}`} points={pts(t.points)} fill="none" stroke="#2E7D32" strokeWidth={1.5 * u} transform={`translate(${4 * u} ${4 * u})`} />))}
      {v.bus.flatMap((b) => b.ordre.map((rep, i) => {
        const x = v.unites.find((y) => y.repere === rep && y.planId === planId);
        return x ? <g key={`${b.systemeId}-${rep}`}><circle cx={x.x + 22 * u} cy={x.y + 18 * u} r={8 * u} fill="#2E7D32" /><text x={x.x + 22 * u} y={x.y + 22 * u} fontSize={10 * u} textAnchor="middle" fill="#FFFFFF" fontWeight={700}>{i + 1}</text></g> : null;
      }))}
    </g>,
  );

  if (actifs.has('points')) {
    const ici2 = points.filter((p) => p.ou?.planId === planId);
    const dec = decalages(ici2, 30 * u);
    couches.push(
      <g key="points" className="prp-c-points">
        {ici2.map((p) => {
          const n = dec.get(p.id) ?? 0; const x = p.ou!.x + n * 26 * u; const y = p.ou!.y + 24 * u;
          const sel = pointChoisi === p.id;
          return (
            <g key={p.id} className="prp-pastille" role="button" tabIndex={0} aria-label={`Point ${p.numero} (${p.niveau === 'bloquant' ? 'bloquant' : 'à vérifier'}) : ${p.message}`}
              onClick={(e) => { e.stopPropagation(); choisirPoint(p.id); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choisirPoint(p.id); } }}>
              <circle cx={x} cy={y} r={20 * u} fill="transparent" />
              <circle cx={x} cy={y} r={(sel ? 13 : 11) * u} fill={p.niveau === 'bloquant' ? '#B42318' : '#FFF1D6'} stroke={p.niveau === 'bloquant' ? '#FFFFFF' : '#8A4B00'} strokeWidth={(sel ? 3 : 2) * u} />
              <text x={x} y={y + 4 * u} fontSize={11 * u} textAnchor="middle" fontWeight={700} fill={p.niveau === 'bloquant' ? '#FFFFFF' : '#8A4B00'}>{p.numero}</text>
            </g>
          );
        })}
      </g>,
    );
  }
  return <>{couches}</>;
}

/** Résumé d'une pièce pour la fiche de sélection. */
export function resumePiece(v: VarianteProposition, pieceId: string) {
  const b = v.besoins.find((x) => x.pieceId === pieceId); const a = v.affectations.find((x) => x.pieceId === pieceId);
  return { besoin: b, affectation: a, etat: etatPiece(b, a), chaud: a?.couverture.chaud != null ? pct(a.couverture.chaud) : null };
}
